import React, { useEffect, useState } from 'react';
import { Flex, useToast } from '@stepstone-genesis/components';
import { ThumbsDownIcon, ThumbsUpIcon } from '@stepstone-genesis/icons';
import { helpers } from 'modules/hub/components/AMAChat/utils/helpers';
import { FilledThumbsUpIcon, FilledThumbsDownIcon, } from 'modules/hub/components/AMAChat/shared/icons';
import DislikeForm from './components/DislikeForm';
import FeedbackButton from './components/FeedbackButton';
import labels from 'modules/hub/components/AMAChat/constants/labels/ama';
import { trackEvent } from 'client/utils/tracking-global';
import { useFeedback } from 'modules/hub/components/AMAChat/hooks/useFeedback';
const Feedback = ({ lang, companyId, sessionId, turn, userMessageNumber, thumbs, dislikeOption, amaConversationID, }) => {
    const [feedbackStatus, setFeedbackStatus] = useState(undefined);
    const { feedbackTypes, loadFeedbackTypes, submitFeedback, deleteFeedback, isLoading, error, } = useFeedback(companyId);
    // Disable like button while dislike form is open; dislike button stays enabled to allow closing the form
    const shouldDisableLikeButton = feedbackStatus === 'dislikeForm';
    const feedbackToastId = `feedback-thankyou-${helpers.rand()}`;
    const showFeedbackToast = () => {
        add({
            content: labels[lang].feedbackThankYouMessage,
            placement: 'bottom-right',
            variant: 'positive',
            duration: 2000,
            onDismiss: onToastDismiss,
            id: feedbackToastId,
        });
    };
    const { add, remove } = useToast();
    const onToastDismiss = () => {
        remove(feedbackToastId);
    };
    const handleLike = async () => {
        if (feedbackStatus === 'likeConfirmed') {
            // Re-click: undo the like
            setFeedbackStatus(undefined);
            await deleteFeedback({ sessionId, turn });
            return;
        }
        setFeedbackStatus('likeConfirmed');
        const result = await submitFeedback({
            sessionId,
            turn,
            feedback: true,
        });
        if (result.success) {
            showFeedbackToast();
            trackEvent({ componentId: 'askMeAnything' }, {
                componentState: 'feedback',
                elementActioned: 'Like',
                elementAction: 'click',
                elementActionValue: `message${userMessageNumber}`,
                amaConversationID: amaConversationID || 'noValue',
            });
        }
        else {
            console.error('[Feedback] Like API error (bypassed):', result.error);
            // Keep status as 'likeConfirmed' to show feedback was recorded
        }
    };
    const handleDislike = async () => {
        if (feedbackStatus === 'dislikeForm') {
            // Re-click while form is open: close the form
            setFeedbackStatus(undefined);
            return;
        }
        if (feedbackStatus === 'dislikeConfirmed') {
            // Re-click: undo the dislike
            setFeedbackStatus(undefined);
            await deleteFeedback({ sessionId, turn });
            return;
        }
        setFeedbackStatus('dislikeForm');
        // Lazy load feedback types when user clicks thumbs down
        await loadFeedbackTypes();
        trackEvent({ componentId: 'askMeAnything' }, {
            componentState: 'feedback',
            elementActioned: 'Dislike',
            elementAction: 'click',
            elementActionValue: `message${userMessageNumber}`,
            amaConversationID: amaConversationID || 'noValue',
        });
    };
    const handleDislikeFormSubmit = async (feedbackReasonType, feedbackReason) => {
        const result = await submitFeedback({
            sessionId,
            turn,
            feedback: false,
            feedbackReasonType,
            feedbackReason,
        });
        if (!result.success) {
            console.error('[Feedback] Dislike API error (bypassed):', result.error);
            // Don't throw error - just close the form normally
        }
    };
    useEffect(() => {
        if (thumbs === 'up') {
            setFeedbackStatus('likeConfirmed');
        }
        else if (thumbs === 'down') {
            setFeedbackStatus('dislikeConfirmed');
        }
    }, [thumbs]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Flex, { flxJustify: "flex-start", gap: "spaceS", marginTop: "spaceS" },
            React.createElement(FeedbackButton, { a11yLabel: labels[lang].likeA11y, filled: feedbackStatus === 'likeConfirmed', iconEmpty: React.createElement(ThumbsUpIcon, { size: "small" }), iconFilled: React.createElement(FilledThumbsUpIcon, null), isDisabled: shouldDisableLikeButton, handleClick: handleLike }),
            React.createElement(FeedbackButton, { a11yLabel: labels[lang].dislikeA11y, filled: feedbackStatus === 'dislikeConfirmed' ||
                    feedbackStatus === 'dislikeForm', iconEmpty: React.createElement(ThumbsDownIcon, { size: "small" }), iconFilled: React.createElement(FilledThumbsDownIcon, null), isDisabled: false, handleClick: handleDislike })),
        feedbackStatus === 'dislikeForm' && (React.createElement(DislikeForm, { lang: lang, setFeedbackStatus: setFeedbackStatus, feedbackTypes: feedbackTypes, isLoadingFeedbackTypes: isLoading, feedbackTypesError: error, onSubmit: handleDislikeFormSubmit, dislikeOption: dislikeOption, onShowFeedbackToast: showFeedbackToast, userMessageNumber: userMessageNumber, amaConversationID: amaConversationID || 'noValue' }))));
};
export default Feedback;
